import { FolderUp, ImagePlus, UploadCloud } from "lucide-react";
import { type DragEvent, useMemo, useRef, useState } from "react";
import { QueueList } from "@/components/capture/QueueList";
import { EnumSelect } from "@/components/shared/enum-select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useCaptureProjects } from "@/hooks/use-capture";
import { useChecklist } from "@/hooks/use-checklist";
import { useUploadQueue } from "@/hooks/use-upload-queue";
import { refusalReason, UPLOAD_ACCEPT, uploadTypeOf } from "@/lib/photo-types";
import { cn } from "@/lib/utils";
import type { ProjectDetail } from "@/schemas/photos";

const NO_ITEM = "__none__";

interface Skipped {
  id: string;
  name: string;
  reason: string;
}
const FOLDER_DROP_HINT = "Folders cannot be dropped here; use Choose a folder";
const photosLabel = (count: number) =>
  `${count.toLocaleString("en-US")} ${count === 1 ? "photo" : "photos"}`;

interface Props {
  project: ProjectDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The album the Photos tab is filtered to, if any. */
  defaultAlbumId: string | undefined;
}

/** Bulk upload from a computer. The photos go through the same queue the phone uses. */
export function UploadPhotosDialog({ project, open, onOpenChange, defaultAlbumId }: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <UploadForm
          project={project}
          defaultAlbumId={defaultAlbumId}
          onDone={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function UploadForm({
  project,
  defaultAlbumId,
  onDone,
}: {
  project: ProjectDetail;
  defaultAlbumId: string | undefined;
  onDone: () => void;
}) {
  const queue = useUploadQueue();
  const filesInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [skipped, setSkipped] = useState<Skipped[]>([]);
  const [dragging, setDragging] = useState(false);
  // Every album of this project, including ones with no photos yet (the project page lists
  // only albums that have photos).
  const captureProjects = useCaptureProjects();
  const albums = useMemo(
    () => captureProjects.data?.find((p) => p.id === project.id)?.albums ?? [],
    [captureProjects.data, project.id],
  );
  const [pickedAlbumId, setAlbumId] = useState<string | null>(null);
  const albumId =
    pickedAlbumId ?? (albums.some((a) => a.id === defaultAlbumId) ? (defaultAlbumId ?? "") : "");
  const [itemId, setItemId] = useState(NO_ITEM);
  // Null until Upload is pressed; then the ids of this batch in the queue.
  const [batch, setBatch] = useState<string[] | null>(null);
  const [problems, setProblems] = useState<string[]>([]);
  const [starting, setStarting] = useState(false);
  // Ids of this batch ever seen in the queue, and ever seen sent (rows can leave the queue).
  const seen = useRef(new Set<string>());
  const sentIds = useRef(new Set<string>());

  const checklist = useChecklist(project.id, project.checklist !== null);
  const album = albums.find((a) => a.id === albumId);
  const items = checklist.data?.albums.find((a) => a.albumId === albumId)?.items ?? [];

  function add(picked: File[]) {
    const accepted: File[] = [];
    const refused: Skipped[] = [];
    for (const file of picked) {
      if (uploadTypeOf(file)) accepted.push(file);
      else refused.push({ id: crypto.randomUUID(), name: file.name, reason: refusalReason(file) });
    }
    setSkipped((current) => [...current, ...refused]);
    setFiles((current) => [...current, ...accepted]);
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    setDragging(false);
    // A dropped folder shows up in `files` as one empty entry; name it for what it is.
    const folders: Skipped[] = [];
    const picked: File[] = [];
    for (const item of Array.from(event.dataTransfer.items)) {
      if (item.kind !== "file") continue;
      const file = item.getAsFile();
      if (!file) continue;
      if (item.webkitGetAsEntry?.()?.isDirectory) {
        folders.push({ id: crypto.randomUUID(), name: file.name, reason: FOLDER_DROP_HINT });
      } else {
        picked.push(file);
      }
    }
    if (folders.length > 0) setSkipped((current) => [...current, ...folders]);
    add(picked);
  }

  async function start() {
    if (!album || files.length === 0) return;
    const item = items.find((i) => i.id === itemId) ?? null;
    setStarting(true);
    const ids: string[] = [];
    const failed: string[] = [];
    for (const file of files) {
      try {
        ids.push(
          await queue.enqueue({
            projectId: project.id,
            projectName: project.name,
            albumId: album.id,
            albumName: album.name,
            itemId: item?.id ?? null,
            itemName: item?.name ?? null,
            via: "web",
            blob: file,
            fileName: file.name,
            mime: uploadTypeOf(file) ?? file.type,
            capturedAt: null,
            lat: null,
            lng: null,
            accuracy: null,
            device: null,
          }),
        );
      } catch (error) {
        failed.push(`${file.name}: ${error instanceof Error ? error.message : "Could not queue"}`);
      }
    }
    setProblems(failed);
    setBatch(ids);
    setFiles([]);
    setStarting(false);
  }

  if (batch) {
    const mine = queue.items.filter((item) => batch.includes(item.id));
    // Rows leave the queue when discarded or cleared after sending; a sent row stays counted.
    for (const item of mine) {
      seen.current.add(item.id);
      if (item.status === "sent") sentIds.current.add(item.id);
    }
    const sent = sentIds.current.size;
    const pending = mine.filter(
      (i) => i.status !== "sent" && i.status !== "failed" && i.status !== "duplicate",
    );
    const gone = batch.filter((id) => seen.current.has(id) && !mine.some((i) => i.id === id));
    const finished = pending.length === 0 && mine.length + gone.length === batch.length;
    return (
      <>
        <DialogHeader>
          <DialogTitle>Uploading photos</DialogTitle>
          <DialogDescription>
            {album?.name}. You can close this; the upload keeps going.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5" aria-live="polite">
            <p className="text-sm font-medium tabular-nums">
              {sent.toLocaleString("en-US")} of {batch.length.toLocaleString("en-US")} uploaded
            </p>
            <div
              role="progressbar"
              aria-label="Overall upload"
              aria-valuemin={0}
              aria-valuemax={batch.length}
              aria-valuenow={sent}
              className="h-2 overflow-hidden rounded-full bg-muted"
            >
              <div
                className="h-full rounded-full bg-[var(--brand-accent)] transition-[width]"
                style={{ width: `${batch.length === 0 ? 0 : (sent / batch.length) * 100}%` }}
              />
            </div>
          </div>
          {problems.length > 0 && (
            <ul className="space-y-1 text-sm text-destructive">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          )}
          <QueueList ids={batch} />
        </div>
        <DialogFooter>
          <Button onClick={onDone}>{finished ? "Done" : "Close"}</Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Upload photos</DialogTitle>
        <DialogDescription>
          Add photos from this computer to {project.name}. Time and location are read from each
          file.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div
          data-testid="upload-drop-zone"
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={cn(
            "flex flex-col items-center gap-3 rounded-lg border-2 border-dashed px-4 py-8 text-center",
            dragging && "border-[var(--brand-accent)] bg-muted",
          )}
        >
          <UploadCloud className="size-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Drag photos here, or</p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button variant="outline" onClick={() => filesInput.current?.click()}>
              <ImagePlus />
              Choose photos
            </Button>
            <Button variant="outline" onClick={() => folderInput.current?.click()}>
              <FolderUp />
              Choose a folder
            </Button>
          </div>
          <input
            ref={filesInput}
            data-testid="upload-files-input"
            type="file"
            accept={UPLOAD_ACCEPT}
            multiple
            className="hidden"
            onChange={(event) => {
              add(Array.from(event.target.files ?? []));
              event.target.value = "";
            }}
          />
          <input
            ref={(element) => {
              folderInput.current = element;
              // React has no prop for it; this is how a file input is told to take a folder.
              element?.setAttribute("webkitdirectory", "");
            }}
            data-testid="upload-folder-input"
            type="file"
            multiple
            className="hidden"
            onChange={(event) => {
              add(Array.from(event.target.files ?? []));
              event.target.value = "";
            }}
          />
        </div>
        {(files.length > 0 || skipped.length > 0) && (
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span>
              <strong className="tabular-nums">{photosLabel(files.length)} ready</strong>
              {skipped.length > 0 && (
                <span className="text-muted-foreground">
                  {" "}
                  · {skipped.length.toLocaleString("en-US")} other{" "}
                  {skipped.length === 1 ? "file" : "files"} skipped
                </span>
              )}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFiles([]);
                setSkipped([]);
              }}
            >
              Clear
            </Button>
          </div>
        )}
        {skipped.length > 0 && (
          <ul data-testid="upload-skipped" className="space-y-0.5 text-xs text-muted-foreground">
            {skipped.slice(0, 5).map((entry) => (
              <li key={entry.id}>
                {entry.name}: {entry.reason}
              </li>
            ))}
            {skipped.length > 5 && <li>and {skipped.length - 5} more</li>}
          </ul>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Album</Label>
            <EnumSelect
              aria-label="Album"
              className="w-full"
              value={albumId}
              onValueChange={(next) => {
                setAlbumId(next);
                setItemId(NO_ITEM);
              }}
              options={albums.map((a) => ({ value: a.id, label: a.name }))}
              placeholder={captureProjects.isPending ? "Loading albums" : "Choose an album"}
            />
          </div>
          {items.length > 0 && (
            <div className="space-y-1.5">
              <Label>Checklist item</Label>
              <EnumSelect
                aria-label="Checklist item"
                className="w-full"
                value={itemId}
                onValueChange={setItemId}
                options={[
                  { value: NO_ITEM, label: "No item" },
                  ...items.map((i) => ({ value: i.id, label: i.name })),
                ]}
              />
            </div>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button variant="ghost" onClick={onDone}>
          Cancel
        </Button>
        <Button disabled={!album || files.length === 0 || starting} onClick={() => void start()}>
          Upload {photosLabel(files.length)}
        </Button>
      </DialogFooter>
    </>
  );
}
